import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchAnswerAudio,
  useSaveReview,
  type AnswerReview,
} from "@/api/tests-api";
import { DictationPlayer } from "@/components/tests/DictationPlayer";
import { ReadingRoom } from "@/components/viewer/ReadingRoom";
import { useAuthStore } from "@/store/auth-store";

/**
 * The candidate's cases, read in the reading room, with their dictation
 * beside the images -- docs/scope-reading-room.md, phase 5.
 *
 * A reviewer scoring "did they describe what is there" is doing the
 * candidate's job a second time, so they read the case with the tools a
 * radiologist reads with: windowing, measurement, reformats, the stack at
 * full size. The room's first panel tab is the review itself -- which
 * cases were recorded, the recording, and the note -- so listening and
 * looking happen on one screen.
 *
 * IT REPLACED A FULL-SCREEN PAGE of the app's own chrome with the
 * diagnostic viewer in it, which itself replaced an inline panel in the
 * screening console: a reading column too narrow to read a CT in. The
 * reason for both moves is the same -- a radiologist cannot score what
 * they cannot see.
 *
 * The room is keyed on the case: stepping to the next case is a fresh
 * room, so window, zoom and measurements never carry from one case to
 * another. Escape, or "Back to the screening", returns to the console.
 */
export function CaseReviewer({
  answers,
  initialIndex,
  attemptId,
  canEdit,
  onClose,
}: {
  answers: AnswerReview[];
  initialIndex: number;
  attemptId: string;
  /** False when the screening belongs to another reviewer. */
  canEdit: boolean;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const current = answers[index];
  const save = useSaveReview();

  // The caller's own note on this case. Everyone's are shown; only one
  // is editable, which is the same rule the sheet follows. Matched on
  // the reviewer id rather than a server flag: this is attribution, not
  // permission -- the server still decides who may write.
  const meId = useAuthStore((st) => st.user?.id ?? null);
  const mine = useMemo(
    () => current?.reviews.find((r) => r.reviewer_user_id === meId) ?? null,
    [current, meId],
  );
  const [note, setNote] = useState(mine?.comments ?? "");
  const [saved, setSaved] = useState<"idle" | "saving" | "done">("idle");
  const timer = useRef<number | null>(null);

  // Reset to whatever is stored WHEN THE CASE CHANGES, or a note typed
  // on case 1 would appear to belong to case 2.
  //
  // Keyed on the answer alone. Depending on ``mine.comments`` as well
  // looked more correct and was not: a successful save invalidates the
  // board, the stored comment changes to what was just written, and the
  // effect fired -- wiping "Saved." back to the idle hint before anyone
  // could read it, and clobbering anything typed while the request was
  // in flight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setNote(mine?.comments ?? "");
    setSaved("idle");
  }, [current?.answer_id]);

  // Debounced, like the candidate's own form: a reviewer types a
  // sentence, not a keystroke, and a PUT per character would be a
  // request storm against a route that upserts.
  const commit = (text: string) => {
    setNote(text);
    if (!current?.answer_id) return;
    if (timer.current !== null) window.clearTimeout(timer.current);
    setSaved("saving");
    timer.current = window.setTimeout(() => {
      save.mutate(
        {
          attemptId,
          answerId: current.answer_id as string,
          body: {
            score: null,
            max_score: null,
            outcome: null,
            comments: text.trim() === "" ? null : text,
          },
        },
        { onSuccess: () => setSaved("done"), onError: () => setSaved("idle") },
      );
    }, 800);
  };

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  if (!current) return null;

  const review = (
    <div data-cy="case-reviewer" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Which cases were RECORDED rather than which are answered,
          because a silent case is the thing a reviewer is looking for. */}
      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <h3 className="rr-h2">Cases</h3>
          <span className="rr-muted" data-cy="review-case-position">
            {answers.filter((a) => a.has_audio).length} of {answers.length} recorded
          </span>
        </div>
        <div className="rr-list" data-cy="review-case-nav">
          {answers.map((a, i) => (
            <button
              key={a.assignment_case_id}
              type="button"
              className="rr-case-item"
              aria-current={i === index ? "true" : undefined}
              onClick={() => setIndex(i)}
            >
              <span className="rr-case-item-title">
                {a.position + 1}. {a.title}
              </span>
              {!a.has_audio && (
                <span className="rr-silent" title="No recording for this case">
                  silent
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 className="rr-h2">Their dictation</h3>
        {current.has_audio && current.answer_id ? (
          // The player is the app's own component, in the app's light
          // palette; it sits on a card of its own rather than being
          // restyled here, so the reviewer's player stays one component.
          <div className="rr-embed">
            {/* Remounted per case: the player caches the object URL it
                fetched, and the audio behind it changes. */}
            <DictationPlayer
              key={current.answer_id}
              load={() => fetchAnswerAudio(current.answer_id as string)}
              durationHint={current.duration_seconds}
            />
          </div>
        ) : (
          <p className="rr-muted" style={{ margin: 0, fontStyle: "italic" }}>
            The candidate recorded nothing for this case.
          </p>
        )}
      </section>

      {/* THE NOTE ON THIS DICTATION, and it is part of the record: the
          print path carries every case's note, not just the one on
          screen. Saved against the ANSWER, so it travels with the
          recording it is about rather than with the sheet as a whole. */}
      <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label className="rr-h2" htmlFor="case-review-note">
          Your comment on this dictation
        </label>
        <textarea
          id="case-review-note"
          data-cy="case-review-note"
          className="rr-textarea"
          placeholder={canEdit ? "What did they cover, what did they miss, how did it read?" : ""}
          value={note}
          disabled={!canEdit || !current.answer_id}
          onChange={(e) => commit(e.target.value)}
        />
        <p className="rr-muted" style={{ margin: 0, fontSize: 12 }} data-cy="case-review-note-status">
          {!current.answer_id
            ? "There is no recording to comment on."
            : !canEdit
              ? "This screening belongs to another reviewer."
              : saved === "saving"
                ? "Saving…"
                : saved === "done"
                  ? "Saved. It appears on the printed record."
                  : "Saved as you type, and printed with the sheet."}
        </p>
        {current.reviews
          .filter((r) => r.reviewer_user_id !== meId && r.comments)
          .map((r) => (
            <p key={r.id} className="rr-note" style={{ margin: 0, fontSize: 12, display: "block" }}>
              <span style={{ fontWeight: 500, color: "#e4e8ee" }}>{r.reviewer_name ?? "A reviewer"}:</span>{" "}
              <span style={{ color: "#a9b4c2" }}>{r.comments}</span>
            </p>
          ))}
      </section>
    </div>
  );

  return (
    <ReadingRoom
      key={current.assignment_case_id}
      source={{ kind: "library", caseId: current.test_case_id }}
      testCase={{
        title: current.title,
        modality: current.modality,
        clinical_history: current.clinical_history,
        body_part: current.body_part ?? null,
        patient_sex: current.patient_sex ?? null,
        patient_age: current.patient_age ?? null,
      }}
      onClose={onClose}
      closeLabel="Back to the screening"
      caseNav={{
        position: index + 1,
        count: answers.length,
        onPrev: () => setIndex((i) => Math.max(0, i - 1)),
        onNext: () => setIndex((i) => Math.min(answers.length - 1, i + 1)),
      }}
      extraTab={{ label: "Dictation", content: review }}
    />
  );
}
